import type { TNavItems, TNavMain } from "@/models/navbar.model"
import { useQuery } from "@tanstack/react-query"
import {
  BarChart3,
  Bell,
  BookOpen,
  Calendar,
  Cog,
  Database,
  Group,
  ListOrdered,
  LucideDumbbell,
  MapPin,
  MessageCircleQuestionIcon,
  Monitor,
  Palette,
  Settings,
  Trophy,
  Tv,
  Users,
  Volleyball,
} from "lucide-react"
import { useTranslations } from "next-intl"
import { useParams, usePathname } from "next/navigation"
import { useMemo } from "react"
import { DisciplineQueryOptions, EventQueryOptions, PhaseQueryOptions } from "@/queries/disciplines.factory"

export function useRoutes() {
  const t = useTranslations("navbar")
  const pathname = usePathname()
  const params = useParams()
  const { rscCode, unitCode } = params
  const standarizedCode = rscCode ? (rscCode as string).substring(0, 26).padEnd(34, "-") : unitCode ? (unitCode as string).substring(0, 26).padEnd(34, "-") : undefined
  const tVenues = useTranslations("venues")
  const tDiscipline = useTranslations("discipline.menu")
  const tGeneralScreen = useTranslations("general_screen")
  const tAmbassador = useTranslations("wrs.ambassador")
  const tPlace = useTranslations("wrs.place")
  const tUser = useTranslations("wrs.user")
  const tAnnouncement = useTranslations("wrs.announcement")
  const { data: disciplines } = useQuery(DisciplineQueryOptions.all)
  const { data: events } = useQuery(EventQueryOptions.all)
  const { data: phases } = useQuery(PhaseQueryOptions.byCode(params.eventId as string))
  const disciplineItems: TNavItems[] | undefined = useMemo(() => {
    return disciplines?.map(discipline => ({
      key: discipline.code,
      title: discipline.description.long,
      url: `/settings/${discipline.code}`,
      description: discipline.description.long,
      icon: LucideDumbbell,
      subItems: [
        ...events?.filter(event => event.metadata.discipline === discipline.code)
          ?.filter((event, index, self) => self.findIndex(e => e.code === event.code) === index)
          ?.map((event) => {
            const phase: TNavItems = {
              key: `${event.code}-${event.metadata.modality}`,
              title: event.description.long as string,
              url: `/settings/${discipline.code}/${event.code}`,
              description: event.description.long as string,
              icon: Calendar,
              subItems: [
                ...phases?.filter(phase => phase.metadata.sportEvent === event.code)?.map(phase => ({
                  key: phase.code,
                  title: phase.description.long,
                  url: `/settings/${discipline.code}/${event.code}/${phase.code}`,
                  description: phase.description.long,
                  icon: Calendar,
                })) ?? [],
              ],
            }
            return phase
          }) ?? [],
      ],
    })) ?? undefined
  }, [disciplines, events, phases])

  const routes: TNavMain[] = [
    {
      key: "DISCIPLINES",
      title: t("disciplines"),
      url: "/disciplines",
      description: t("disciplines"),
      icon: Volleyball,
      isActive: true,
      hidden: false,
      code: "DISC_VIEW",
      order: 1,
      items: [
        ...disciplines?.map(discipline => ({
          key: discipline.code,
          title: discipline.description.long,
          url: `/disciplines/${discipline.code}`,
          description: discipline.description.long,
          icon: LucideDumbbell,
          subItems: [
            {
              key: "rules",
              title: tDiscipline("rules.title"),
              url: `/disciplines/${discipline.code}/rules`,
              description: tDiscipline("rules.description"),
              icon: BookOpen,
            },
            {
              key: "settings",
              title: tDiscipline("settings.title"),
              url: `/settings/?discipline=${discipline.code}`,
              description: tDiscipline("settings.description"),
              icon: Settings,
            },
            {
              key: "participants",
              title: tDiscipline("participants.title"),
              url: `/disciplines/${discipline.code}/participants`,
              description: tDiscipline("participants.description"),
              icon: Users,
            },
            {
              key: "groups",
              title: tDiscipline("groups.title"),
              url: `/disciplines/${discipline.code}/groups`,
              description: tDiscipline("groups.description"),
              icon: Group,
            },
            {
              key: "start_list",
              title: tDiscipline("start_list.title"),
              url: `/disciplines/${discipline.code}/start-list/all-units`,
              description: tDiscipline("start_list.description"),
              icon: ListOrdered,
            },
            {
              key: "data_entry",
              title: tDiscipline("data_entry.title"),
              url: `/disciplines/${discipline.code}/data-entry/all-units`,
              description: tDiscipline("data_entry.description"),
              icon: Database,
            },
            {
              key: "reports",
              title: tDiscipline("reports.title"),
              url: `/disciplines/${discipline.code}/reports`,
              description: tDiscipline("reports.description"),
              icon: BarChart3,
            },
            {
              key: "medals",
              title: tDiscipline("medals.title"),
              url: `/settings/${discipline.code}/medals`,
              description: tDiscipline("medals.description"),
              icon: Trophy,
            },
            {
              key: "channels",
              title: tDiscipline("channels.title"),
              url: `/disciplines/${discipline.code}/channels`,
              description: tDiscipline("channels.description"),
              icon: Monitor,
            },
            {
              key: "simple-data-entry",
              title: tDiscipline("simple_data_entry.title"),
              url: `/disciplines/${discipline.code}/simple-data-entry/all-units`,
              description: tDiscipline("simple_data_entry.description"),
              icon: Database,
            },
          ],
        })) ?? [],
      ],
    },
    {
      key: "SETTINGS",
      title: t("settings"),
      url: "/settings",
      description: t("settings_description"),
      icon: Cog,
      isActive: true,
      hidden: false,
      code: "SETT_VIEW",
      order: 3,
      items: [
        ...(disciplineItems ?? []),
      ],
    },
    {
      key: "USERS",
      title: t("users"),
      url: "/users",
      description: t("users_description"),
      icon: Users,
      isActive: true,
      hidden: false,
      code: "USER_VIEW", // TODO: De momento esta esto, cambiarlo por la respuesta del user
      order: 2,
    },
    {
      key: "VENUES",
      title: tVenues("title"),
      url: "/venues",
      description: tVenues("description"),
      icon: MapPin,
      isActive: true,
      hidden: false,
      code: "VENUE_VIEW",
      order: 3,
    },
    {
      key: "WRS",
      title: t("web_results.title"),
      url: "/web-results",
      description: t("web_results.description"),
      icon: Users,
      isActive: true,
      hidden: false,
      items: [
        {
          key: "CALENDAR",
          title: t("web_results.calendar.title"),
          url: "/web-results/calendar",
          description: t("web_results.calendar.description"),
          icon: Calendar,
        },
        {
          key: "AMBASSADORS",
          title: tAmbassador("title"),
          url: "/web-results/ambassadors",
          description: tAmbassador("title"),
          icon: Users,
        },
        {
          key: "ANNOUNCEMENTS",
          title: tAnnouncement("title"),
          url: "/web-results/announcements",
          description: tAnnouncement("description"),
          icon: Bell,
        },
        {
          key: "USERS",
          title: tUser("title"),
          url: "/web-results/users",
          description: tUser("title"),
          icon: Users,
        },
        {
          key: "PLACES",
          title: tPlace("title"),
          url: "/web-results/places",
          description: tPlace("title"),
          icon: MapPin,
        },
        {
          key: "CONFIG",
          title: t("web_results.config.title"),
          url: "/web-results/config",
          description: t("web_results.config.description"),
          icon: Cog,
        },
        {
          key: "THEMES",
          title: t("web_results.themes.title"),
          url: "/web-results/themes",
          description: t("web_results.themes.description"),
          icon: Palette,
        },
        {
          key: "TRIVIA",
          title: t("web_results.trivia.title"),
          url: "/web-results/trivia",
          description: t("web_results.trivia.description"),
          icon: MessageCircleQuestionIcon,
        },
      ],
      code: "WEB_RESULTS_VIEW",
      order: 4,
    },
    {
      key: "GENERAL_SCREEN",
      title: tGeneralScreen("title"),
      url: "/general-screen",
      description: tGeneralScreen("description"),
      icon: Tv,
      isActive: true,
      hidden: false,
      code: "GENERAL_SCREEN_VIEW",
      order: 5,
    },
    // {
    //   key: "REPORTS",
    //   title: t("reports"),
    //   url: "/reports",
    //   description: t("reports"),
    //   icon: Users,
    //   isActive: true,
    //   hidden: false,
    // },

  ]
  return routes.sort((a, b) => a.order - b.order)
}
